import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Lock, Star, Search, BookOpen } from 'lucide-react';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'basic', label: 'BASIC', color: 'from-blue-600 to-blue-800' },
  { key: 'comps_ready', label: "COMP'S READY", color: 'from-purple-600 to-purple-800' },
  { key: 'advanced', label: 'ADVANCED', color: 'from-orange-600 to-orange-800' }
];

export default function ClassCenter() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`);
      setClasses(res.data);
    } catch (error) {
      console.error('Failed to load classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter(cls => {
    const matchesSearch = !searchTerm || 
      cls.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = !selectedTier || cls.tier === selectedTier;
    
    return matchesSearch && matchesTier;
  });

  const groupedByTier = TIERS.reduce((acc, tier) => {
    acc[tier.key] = filteredClasses.filter(cls => cls.tier === tier.key);
    return acc;
  }, {});

  const handleClassClick = (classId) => {
    navigate(`/classes/${classId}`);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-16 mb-12">
        <div className="container mx-auto px-6 text-center">
          <GraduationCap className="w-20 h-20 mx-auto mb-6" />
          <h1 className="text-6xl font-bold retro-title mb-6">
            CLASS CENTER
          </h1>
          <p className="text-xl max-w-4xl mx-auto leading-relaxed">
            Explore comprehensive classes with structured lessons, study materials, and expert instruction
          </p>
        </div>
      </div>

      {/* Quote Position 1 - Above Main Content */}
      <PageQuote pageName="class_center" position={1} variant="compact" />

      {/* Search and Filters */}
      <div className="container mx-auto px-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search classes..."
              className="w-full pl-10 pr-4 py-3 border-2 border-[#8B5A3C] rounded-lg focus:outline-none focus:border-[#6B4423]"
            />
          </div>
          
          <select
            value={selectedTier || ''}
            onChange={(e) => setSelectedTier(e.target.value || null)}
            className="px-4 py-3 border-2 border-[#8B5A3C] rounded-lg focus:outline-none focus:border-[#6B4423] bg-white"
          >
            <option value="">All Tiers</option>
            {TIERS.map(tier => (
              <option key={tier.key} value={tier.key}>{tier.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Classes by Tier */}
      <div className="container mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFA500] mx-auto"></div>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">No classes found</p>
          </div>
        ) : (
          TIERS.map(tier => {
            const tierClasses = groupedByTier[tier.key];
            if (tierClasses.length === 0) return null;

            return (
              <div key={tier.key} className="mb-12">
                <div className={`bg-gradient-to-r ${tier.color} text-white px-6 py-3 rounded-t-lg`}>
                  <h2 className="text-3xl font-bold retro-title">{tier.label}</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {tierClasses.map(cls => (
                    <div
                      key={cls.id}
                      onClick={() => handleClassClick(cls.id)}
                      className="bg-white border-4 border-[#8B5A3C] rounded-lg overflow-hidden hover:shadow-2xl transition-all cursor-pointer transform hover:scale-105"
                    >
                      {/* Cover Image */}
                      <div className="h-48 bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
                        {cls.cover_image_url ? (
                          <img 
                            src={cls.cover_image_url} 
                            alt={cls.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <GraduationCap className="w-24 h-24 text-white opacity-50" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-2xl font-bold text-[#2C1810] mb-2 line-clamp-2">
                          {cls.title}
                        </h3>
                        
                        <p className="text-gray-700 mb-4 line-clamp-3">
                          {cls.description}
                        </p>

                        {/* Lessons Count */}
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            <span>{cls.lesson_ids?.length || 0} Lessons</span>
                          </div>
                          
                          {cls.membership_required && !cls.is_free && (
                            <div className="flex items-center gap-1 text-orange-600">
                              <Lock className="w-4 h-4" />
                              <span className="font-semibold">Membership</span>
                            </div>
                          )}
                          
                          {cls.is_free && (
                            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                              FREE
                            </span>
                          )}
                        </div>

                        {/* Price */}
                        {!cls.is_free && cls.price > 0 && (
                          <div className="text-2xl font-bold text-emerald-600">
                            ${cls.price.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Quote Position 2 - Below Main Content */}
      <PageQuote pageName="class_center" position={2} variant="compact" />
    </Layout>
  );
}
